// The media library — cloud storage for this workspace, and the record of what
// happened to everything in it.
//
// Uploading used to belong to Compose, which made the library a by-product:
// nothing arrived here unless a post was being written. That is backwards for
// anyone who shoots a hundred clips on a trip and captions them over the
// following month. So files land here first, on their own, and Compose picks
// from what is already stored.
//
// Since ADR-0018 the library has folders, because one flat list ordered by
// upload time is unusable for the agency running forty clients out of the same
// workspace. A folder is a label and nothing else: it holds no bytes, and it
// grants no access — everyone who may see the library sees the whole tree.
// Dropping a folder from the desktop keeps its shape, subfolders and all, which
// is the only import an agency actually has.
//
// The other half of a file list is "what happened to this video?", so selecting
// an asset shows the posts it went out in and how each network handled it. An
// upload that was never published is called out too.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createMediaFolder, currentWorkspace, deleteMedia, deleteMediaFolder, importMedia, listMedia,
  listMediaFolders, mediaCounts, mediaFit, moveMediaAssets, moveMediaFolder, renameMediaFolder,
  setMediaBrand, signMedia, thumbPath, thumbTargets, uploadMedia, type MediaScope,
} from "../lib/api";
import {
  entriesFromDrop, filesFromEntries, filesFromInput, formatBytes, inspect, safeFolderName,
  type DroppedFile,
} from "../lib/media";
import {
  formatLimit, isPaywall, limitOf, loadEntitlements, paywallMessage, usedOf,
} from "../lib/entitlements";
import { useWorkspace } from "../lib/WorkspaceContext";
import { useBrands } from "../lib/useBrands";
import type { Brand } from "../lib/brands";
import {
  PLATFORM_LABEL, type Entitlements, type MediaAsset, type MediaFolder, type Platform,
  type ValidationResult,
} from "../lib/types";
import { logError } from "../lib/logger";
import { ContentPolicyNotice } from "../components/ContentPolicyNotice";
import { Thumb } from "../components/Thumb";
import { MediaFitDetail, MediaFitRow } from "../components/MediaFit";
import { formatInZone, formatInZoneWithName } from "../lib/timezone";
import { useZone } from "../lib/userSettings";

type Filter = "all" | "video" | "photo" | "unused";

const isVideo = (a: MediaAsset) => a.mime_type.startsWith("video/");

function duration(seconds: number | null): string | null {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Rows per request. Storage means hundreds of files, not a picker's worth. */
const PAGE = 120;

/** One file on its way in. Kept in order so a big drop reads as a queue. */
interface Upload {
  id: string;
  name: string;
  /** "Clients / BluePeak" — where a dropped folder put it. */
  folder: string | null;
  size: number;
  state: "queued" | "reading" | "filing" | "uploading" | "done" | "error";
  error?: string;
}

/** Which drawer the grid is showing. */
type View =
  | { kind: "all" }
  | { kind: "unfiled" }
  | { kind: "folder"; id: string };

/** A folder and everything below it, for "including subfolders". */
function subtreeIds(folders: MediaFolder[], rootId: string): string[] {
  const children = new Map<string, string[]>();
  for (const f of folders) {
    if (!f.parent_id) continue;
    children.set(f.parent_id, [...(children.get(f.parent_id) ?? []), f.id]);
  }
  const out: string[] = [];
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift()!;
    out.push(id);
    queue.push(...(children.get(id) ?? []));
  }
  return out;
}

/**
 * The scope as one comparable string.
 *
 * The list reloads when the scope changes and the folder tree reloads whenever
 * anything is filed — keying the reload on a string rather than on the array of
 * ids means a tree refresh that changed nothing does not re-fetch the page it
 * just fetched.
 */
function scopeFromKey(key: string): MediaScope {
  if (key === "unfiled") return { kind: "unfiled" };
  if (key.startsWith("f:")) {
    return { kind: "folders", folderIds: key.slice(2).split(",").filter(Boolean) };
  }
  return { kind: "all" };
}

export function Media() {
  const { can } = useWorkspace();
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  // Per-network verdicts, keyed by asset id. Empty array = the check failed.
  const [fit, setFit] = useState<Map<string, ValidationResult[]>>(new Map());
  const [selected, setSelected] = useState<MediaAsset | null>(null);
  // The full-size URL for whatever the detail dialog is showing. The grid signs
  // derivatives (0017-v2 §2), so the original is signed only when somebody
  // actually opens one — which is the whole saving: one original per click
  // instead of a hundred and twenty per page.
  const [fullUrl, setFullUrl] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<Filter>("all");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  // Whose the files are (ADR-0029). A SECOND axis beside folders, not a
  // replacement: the folder says which shoot, the brand says whose. The choice
  // is the shared one the top bar holds, so the library follows the brand the
  // rest of the app is already showing.
  const { brands, activeBrandId, selectBrand } = useBrands(workspaceId);
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [counts, setCounts] = useState({ total: 0, unfiled: 0 });
  const [view, setView] = useState<View>({ kind: "all" });
  const [includeSub, setIncludeSub] = useState(true);
  const [folderDialog, setFolderDialog] =
    useState<{ mode: "create" | "rename"; parent: string | null; folder?: MediaFolder } | null>(null);
  const [folderBusy, setFolderBusy] = useState(false);
  const [confirmFolder, setConfirmFolder] = useState<MediaFolder | null>(null);

  // Filing a hundred files one modal at a time is not filing, so the grid has a
  // selection mode and one Move.
  const [picking, setPicking] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [paywall, setPaywall] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  /** Ids already sent for a fit check — a ref, so asking does not re-render. */
  const fitAsked = useRef<Set<string>>(new Set());
  const folderInput = useRef<HTMLInputElement>(null);

  const mayUpload = can("media.upload");
  const mayDelete = can("media.delete");
  const mayFile = can("media.edit");

  const current = view.kind === "folder"
    ? folders.find((f) => f.id === view.id) ?? null
    : null;
  // Uploads land where you are looking. "All files" and "Unfiled" both mean the
  // workspace root, which is the only place a file can be that is not a folder.
  const uploadFolderId = current?.id ?? null;

  const scopeKey = useMemo(() => {
    if (view.kind === "unfiled") return "unfiled";
    if (view.kind === "all") return "all";
    const ids = includeSub ? subtreeIds(folders, view.id) : [view.id];
    return `f:${ids.join(",")}`;
  }, [view, includeSub, folders]);

  // Keyed separately from `scopeKey` so `scopeFromKey` stays a pure reading of
  // the FOLDER scope. Both are in the reload's dependencies; neither parses the
  // other's half.
  const brandKey = activeBrandId ?? "";

  /** The tree and the two numbers no folder row can show. */
  const refreshFolders = useCallback(async (ws: string) => {
    const [tree, totals] = await Promise.all([
      listMediaFolders(ws),
      mediaCounts(ws).catch(() => ({ total: 0, unfiled: 0 })),
    ]);
    setFolders(tree);
    setCounts(totals);
    return tree;
  }, []);

  const refresh = useCallback(async () => {
    if (!workspaceId) return;
    const rows = await listMedia(workspaceId, PAGE, 0, scopeFromKey(scopeKey), brandKey || null);
    setAssets(rows);
    // A full page back means there is probably another one.
    setMore(rows.length === PAGE);
    // Signed URLs expire; they are fetched per visit rather than stored.
    // The rows, not just their paths: the asset carries the size and the
    // workspace the preview transfer is metered against (ADR-0017).
    setUrls(await signMedia(thumbTargets(rows), { surface: "library" }));
    await refreshFolders(workspaceId).catch(() => {});
    // The meter is the workspace's real consumption, not a sum of this page —
    // the list is paged and would undercount a big library.
    setEnt(await loadEntitlements(workspaceId).catch(() => null));
  }, [workspaceId, scopeKey, brandKey, refreshFolders]);

  /** The next page, appended. Signed URLs are merged rather than re-fetched. */
  const loadMore = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingMore(true);
    try {
      const rows = await listMedia(
        workspaceId, PAGE, assets.length, scopeFromKey(scopeKey), brandKey || null);
      setAssets((prev) => [...prev, ...rows]);
      setMore(rows.length === PAGE);
      const signed = await signMedia(thumbTargets(rows), { surface: "library" });
      setUrls((prev) => new Map([...prev, ...signed]));
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "load_media_page" });
    } finally {
      setLoadingMore(false);
    }
  }, [workspaceId, assets.length, scopeKey, brandKey]);

  useEffect(() => {
    currentWorkspace()
      .then(setWorkspaceId)
      .catch((e) => {
        setError(e.message ?? String(e));
        void logError("app", e, { step: "load_media" });
        setLoading(false);
      });
  }, []);

  // Runs again when the chosen folder changes, which is what makes the tree a
  // navigation rather than a filter over one fetched page.
  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    setChecked(new Set());
    refresh()
      .catch((e) => {
        setError(e.message ?? String(e));
        void logError("app", e, { step: "load_media" });
      })
      .finally(() => setLoading(false));
  }, [workspaceId, refresh]);

  /**
   * Which networks would take each file on screen.
   *
   * Asked once per asset and never again: the verdict is a function of the
   * stored facts and the rules table, and neither changes while the page is
   * open. One request covers the whole page (`mediaFit` chunks), so a library
   * of five hundred files costs four calls rather than five hundred.
   *
   * A failure here is not the library's failure. The row is marked empty, which
   * removes it, and the page carries on doing everything it did before badges
   * existed.
   */
  // The original, signed only when a detail dialog is actually opened. Cleared
  // first so a dialog never shows the previous asset's picture while this
  // resolves.
  useEffect(() => {
    setFullUrl(undefined);
    if (!selected) return;
    let live = true;
    void signMedia([selected], { surface: "library" })
      .then((m) => { if (live) setFullUrl(m.get(selected.storage_path)); })
      .catch(() => {});
    return () => { live = false; };
  }, [selected]);

  useEffect(() => {
    const pending = assets.filter((a) => !fitAsked.current.has(a.id));
    if (pending.length === 0) return;
    for (const a of pending) fitAsked.current.add(a.id);

    void (async () => {
      try {
        const got = await mediaFit(pending);
        setFit((prev) => {
          const next = new Map(prev);
          // An id the function did not answer for still gets an entry, so the
          // row resolves instead of checking forever.
          for (const a of pending) next.set(a.id, got.get(a.id) ?? []);
          return next;
        });
      } catch (e: any) {
        setFit((prev) => {
          const next = new Map(prev);
          for (const a of pending) next.set(a.id, []);
          return next;
        });
        void logError("media_validate", e, {
          step: "media_fit", workspaceId, context: { assets: pending.length },
        });
      }
    })();
  }, [assets, workspaceId]);

  /**
   * Make sure a dropped folder path exists, and answer with the folder the file
   * belongs in.
   *
   * The cache is per batch and keyed by parent: two hundred files from the same
   * shoot ask for the same folder two hundred times, and only the first of them
   * should create anything. A creation that loses a race against another tab
   * re-reads the tree rather than failing the upload — the folder existing is
   * the outcome we wanted either way.
   */
  const ensureFolder = useCallback(async (
    ws: string, names: string[], base: string | null, cache: Map<string, string>,
  ): Promise<string | null> => {
    let parent = base;
    let known = folders;
    for (const raw of names) {
      const name = safeFolderName(raw);
      const key = `${parent ?? "root"}/${name.toLowerCase()}`;
      let id = cache.get(key)
        ?? known.find((f) => (f.parent_id ?? null) === parent
                          && f.name.toLowerCase() === name.toLowerCase())?.id;
      if (!id) {
        try {
          id = (await createMediaFolder(ws, name, parent)).id;
        } catch {
          known = await refreshFolders(ws);
          id = known.find((f) => (f.parent_id ?? null) === parent
                              && f.name.toLowerCase() === name.toLowerCase())?.id;
          if (!id) throw new Error(`Could not create the folder "${name}".`);
        }
      }
      cache.set(key, id);
      parent = id;
    }
    return parent;
  }, [folders, refreshFolders]);

  /**
   * Files in, one at a time.
   *
   * Sequential on purpose. Uploading a hundred files at once would open a
   * hundred connections and, worse, race the storage check: every parallel
   * upload would ask "does this fit?" against the same stale total and they
   * would all be told yes. One at a time means the quota is true each time it
   * is consulted, and the queue below stays a queue people can read.
   */
  const enqueue = useCallback(async (items: DroppedFile[]) => {
    const ws = workspaceId;
    if (!ws || items.length === 0) return;

    const batch: Upload[] = items.map((item, i) => ({
      id: `${Date.now()}-${i}-${item.file.name}`,
      name: item.file.name,
      folder: item.path.length ? item.path.join(" / ") : null,
      size: item.file.size,
      state: "queued",
    }));
    setUploads((q) => [...q, ...batch]);
    setBusy(true);
    setPaywall(null);

    const mark = (id: string, patch: Partial<Upload>) =>
      setUploads((q) => q.map((u) => (u.id === id ? { ...u, ...patch } : u)));

    const folderCache = new Map<string, string>();

    for (let i = 0; i < items.length; i++) {
      const { file, path } = items[i];
      const row = batch[i];
      try {
        let target = uploadFolderId;
        if (path.length > 0) {
          mark(row.id, { state: "filing" });
          target = await ensureFolder(ws, path, uploadFolderId, folderCache);
        }
        mark(row.id, { state: "reading" });
        // Dimensions and duration are read here, in the browser, because that is
        // the only place with a decoder (ADR-0004). Publishing later validates
        // against these numbers without touching the bytes again.
        const facts = await inspect(file);
        mark(row.id, { state: "uploading" });
        // Filed where you are looking, on both axes. The database fills the
        // brand in when the workspace has only one, so this matters from the
        // second brand onwards — which is exactly when it starts mattering.
        await uploadMedia(ws, file, facts, target, activeBrandId);
        mark(row.id, { state: "done" });
      } catch (e: any) {
        const message = isPaywall(e) ? paywallMessage(e) : (e.message ?? String(e));
        mark(row.id, { state: "error", error: message });
        if (isPaywall(e)) {
          // Out of space stops the batch: every remaining file would fail the
          // same way, and a wall of identical errors helps nobody.
          setPaywall(message);
          void logError("paywall", e, { step: "media_upload", workspaceId: ws });
          setUploads((q) =>
            q.map((u) =>
              batch.slice(i + 1).some((b) => b.id === u.id)
                ? { ...u, state: "error", error: "Skipped — no space left." }
                : u));
          break;
        }
        void logError("media_upload", e, { step: "upload", workspaceId: ws });
      }
    }

    setBusy(false);
    await refresh().catch(() => {});
  }, [workspaceId, uploadFolderId, activeBrandId, ensureFolder, refresh]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!mayUpload) return;
    // Synchronously, before any await: the DataTransfer's item list is emptied
    // the moment this handler returns, and a dropped folder is only visible
    // through those entries.
    const entries = entriesFromDrop(e.dataTransfer);
    const fallback = Array.from(e.dataTransfer.files);
    void (async () => {
      const items = entries.length > 0
        ? await filesFromEntries(entries)
        // Safari before 11.1 and anything without the entries API: files only,
        // no structure. Better a flat upload than a refused drop.
        : filesFromInput(fallback);
      await enqueue(items);
    })();
  };

  const shown = useMemo(() => assets.filter((a) => {
    switch (filter) {
      case "video":  return isVideo(a);
      case "photo":  return !isVideo(a);
      case "unused": return (a.posts?.length ?? 0) === 0;
      default:       return true;
    }
  }), [assets, filter]);

  const used = usedOf(ent, "cloud_storage_bytes");
  const limit = limitOf(ent, "cloud_storage_bytes");
  const pct = limit === null || limit === 0 ? 0 : Math.min(100, (used / limit) * 100);
  // Still in flight — "done" and "error" are both finished, and counting a
  // failure as pending would leave the header claiming work that stopped.
  const pending = uploads.filter((u) => u.state !== "done" && u.state !== "error").length;

  const runFolderAction = async (fn: () => Promise<unknown>) => {
    setFolderBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "media_folder", workspaceId });
    } finally {
      setFolderBusy(false);
    }
  };

  const moveChecked = async (folderId: string | null) => {
    const ids = [...checked];
    await runFolderAction(async () => {
      await moveMediaAssets(ids, folderId);
      setChecked(new Set());
    });
  };

  /** The other axis, in bulk. Filing a shoot under a brand is one act, and
   *  doing it forty times is how half a shoot ends up under the wrong one. */
  const brandChecked = async (brandId: string | null) => {
    const ids = [...checked];
    await runFolderAction(async () => {
      await setMediaBrand(ids, brandId);
      setChecked(new Set());
    });
  };

  if (error && !workspaceId) return <div className="card err">{error}</div>;

  return (
    <>
      {/* Same control and same wording as the composer's title: one notion of
          "which brand am I working in", said the same way wherever it is
          offered. Shown only when there is a brand to name. */}
      <h1>
        Media
        {brands.length > 0 && (
          <>
            {" "}for{" "}
            <select
              className="brand-inline"
              aria-label="Brand whose files to show"
              value={activeBrandId ?? ""}
              onChange={(e) => selectBrand(e.target.value || null)}
            >
              <option value="">all brands</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </>
        )}
      </h1>
      <p className="muted" style={{ marginTop: "-.4rem", maxWidth: "62ch" }}>
        Your storage. Upload photos and video here whenever you like — nothing has to be
        published, or even written, for a file to live here. Organise it into folders the way you
        already do on your own machine, then{" "}
        <Link to="/compose">Compose</Link> picks from what is already stored, so a post goes out
        without uploading anything twice.
      </p>

      {/* Not while a dialog is open: the dialogs show their own copy, and the
          same sentence in two places reads as two problems. */}
      {error && !importing && !folderDialog && <div className="card err">{error}</div>}

      <div className="media-layout">
        <aside className="card folder-panel" aria-label="Folders">
          <div className="storage-head">
            <h2>Folders</h2>
            <span className="spacer" />
            {mayFile && (
              <button className="sm" disabled={folderBusy}
                      onClick={() => setFolderDialog({ mode: "create", parent: uploadFolderId })}>
                New
              </button>
            )}
          </div>

          <ul className="folder-tree">
            <li>
              <button className={view.kind === "all" ? "folder-row on" : "folder-row"}
                      onClick={() => setView({ kind: "all" })}>
                <span className="folder-name">All files</span>
                <span className="muted folder-count">{counts.total}</span>
              </button>
            </li>
            <li>
              <button className={view.kind === "unfiled" ? "folder-row on" : "folder-row"}
                      onClick={() => setView({ kind: "unfiled" })}>
                <span className="folder-name">Unfiled</span>
                <span className="muted folder-count">{counts.unfiled}</span>
              </button>
            </li>
            {folders.map((f) => (
              <li key={f.id}>
                <button
                  className={view.kind === "folder" && view.id === f.id ? "folder-row on" : "folder-row"}
                  style={{ paddingLeft: `${0.5 + (f.depth - 1) * 0.85}rem` }}
                  onClick={() => setView({ kind: "folder", id: f.id })}
                  title={f.path}
                >
                  <span className="folder-name">{f.name}</span>
                  {/* The subtree number, because "how much is under this
                      client" is the question, not "how much is loose in it". */}
                  <span className="muted folder-count">{f.subtree_file_count}</span>
                </button>
              </li>
            ))}
          </ul>

          {folders.length === 0 && (
            <p className="muted">
              No folders yet. {mayFile
                ? "Make one for each client or campaign — or just drop a folder from your desktop and its structure is kept."
                : "Everything is in one list."}
            </p>
          )}
        </aside>

        <div className="media-main">
          {/* Where you are, and everything you can do to it. Shown for a folder
              only: "All files" is not a place things can be renamed. */}
          {current && (
            <div className="card folder-bar">
              <div className="folder-crumbs">
                <button className="link" onClick={() => setView({ kind: "all" })}>All files</button>
                {current.path.split(" / ").map((part, i, all) => (
                  <span key={`${part}-${i}`}>
                    <span className="crumb-sep">/</span>
                    {i === all.length - 1
                      ? <strong>{part}</strong>
                      : <span className="muted">{part}</span>}
                  </span>
                ))}
                <span className="muted">
                  {" "}· {current.subtree_file_count} file{current.subtree_file_count === 1 ? "" : "s"}
                  {" "}· {formatBytes(current.subtree_bytes)}
                </span>
              </div>

              <label className="inline-check">
                <input type="checkbox" checked={includeSub}
                       onChange={(e) => setIncludeSub(e.target.checked)} />
                <span>Include subfolders</span>
              </label>

              {mayFile && (
                <div className="row folder-actions">
                  <button className="sm" disabled={folderBusy}
                          onClick={() => setFolderDialog({ mode: "create", parent: current.id })}>
                    New subfolder
                  </button>
                  <button className="sm" disabled={folderBusy}
                          onClick={() => setFolderDialog({
                            mode: "rename", parent: current.parent_id, folder: current,
                          })}>
                    Rename
                  </button>
                  <label className="visually-hidden" htmlFor="move-folder">Move this folder</label>
                  <select id="move-folder" value={current.parent_id ?? ""} disabled={folderBusy}
                          onChange={(e) => void runFolderAction(() =>
                            moveMediaFolder(current.id, e.target.value || null))}>
                    <option value="">Top level</option>
                    {folders
                      // A folder cannot be moved into itself or into anything
                      // below it; the database refuses it and so does this list.
                      .filter((f) => !subtreeIds(folders, current.id).includes(f.id))
                      .map((f) => (
                        <option key={f.id} value={f.id}>{f.path}</option>
                      ))}
                  </select>
                  <button className="sm danger" disabled={folderBusy}
                          onClick={() => setConfirmFolder(current)}>
                    Delete folder
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="card media-toolbar">
            <div className="segmented" role="group" aria-label="Filter media">
              {([
                ["all", more ? `All ${assets.length}+` : `All ${assets.length}`],
                ["video", "Video"],
                ["photo", "Photos"],
                ["unused", "Never published"],
              ] as Array<[Filter, string]>).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={filter === key ? "on" : ""}
                  aria-pressed={filter === key}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="spacer" />
            {mayFile && assets.length > 0 && (
              <button className="sm" aria-pressed={picking}
                      onClick={() => { setPicking(!picking); setChecked(new Set()); }}>
                {picking ? "Done selecting" : "Select"}
              </button>
            )}
            <button className="sm" onClick={() => setImporting(true)}
                    disabled={!workspaceId || !mayUpload}>
              Import from a link
            </button>
            <button className="sm" onClick={() => folderInput.current?.click()}
                    disabled={!workspaceId || !mayUpload || busy}>
              Upload a folder
            </button>
            <button className="sm primary" onClick={() => fileInput.current?.click()}
                    disabled={!workspaceId || !mayUpload || busy}>
              {busy ? "Uploading…" : "Upload files"}
            </button>
          </div>

          {picking && (
            <div className="card select-bar">
              <strong>{checked.size} selected</strong>
              <span className="spacer" />
              <label htmlFor="move-to">Move to</label>
              <select id="move-to" value="" disabled={checked.size === 0 || folderBusy}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value) return;
                        void moveChecked(value === "__root" ? null : value);
                        e.target.value = "";
                      }}>
                <option value="">Choose a folder…</option>
                <option value="__root">Unfiled (workspace root)</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
              </select>
              {brands.length > 0 && (
                <>
                  <label htmlFor="file-under">File under</label>
                  <select id="file-under" value="" disabled={checked.size === 0 || folderBusy}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!value) return;
                            void brandChecked(value === "__none" ? null : value);
                            e.target.value = "";
                          }}>
                    <option value="">Choose a brand…</option>
                    <option value="__none">No brand</option>
                    {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </>
              )}
              <button className="sm" disabled={checked.size === 0}
                      onClick={() => setChecked(new Set())}>Clear</button>
            </div>
          )}

          {/* The meter is why deleting exists: a quota you cannot see fill up is a
              quota you only learn about when an upload is refused. */}
          {ent && (
            <div className="card storage-meter">
              <div className="storage-head">
                <strong>{formatBytes(used)}</strong>
                <span className="muted">
                  {limit === null
                    ? " used — no storage limit on your plan"
                    : ` of ${formatLimit(limit, "bytes")} used`}
                </span>
                <span className="spacer" />
                <Link className="muted" to="/plans">
                  {ent.plan?.name ?? "Your plan"}
                </Link>
              </div>
              {limit !== null && (
                <div className="bar" title={`${formatBytes(used)} of ${formatLimit(limit, "bytes")}`}>
                  <div className={pct >= 90 ? "full" : ""} style={{ width: `${pct}%` }} />
                </div>
              )}
            </div>
          )}

          {paywall && (
            <div className="notice upgrade">
              {paywall} Free up space by deleting files you no longer need, or{" "}
              <Link to="/plans">see plans</Link>.
            </div>
          )}

          {mayUpload && (
            <>
              {/* Standing above the drop zone and the import dialog alike: both are
                  ways in, and the rule is the same for a file nobody publishes. */}
              <ContentPolicyNotice where="library" />
              <input
                ref={fileInput}
                type="file"
                multiple
                accept="image/*,video/*"
                hidden
                onChange={(e) => {
                  void enqueue(filesFromInput(Array.from(e.target.files ?? [])));
                  // Cleared so choosing the same file twice still fires a change.
                  e.target.value = "";
                }}
              />
              {/* The folder equivalent. `webkitdirectory` is not in the React
                  types and is not standardised, but every browser we support
                  implements it, and it is the only way to *choose* a folder —
                  dropping one goes through the entries API instead. */}
              <input
                ref={folderInput}
                type="file"
                multiple
                hidden
                {...{ webkitdirectory: "", directory: "" } as any}
                onChange={(e) => {
                  void enqueue(filesFromInput(Array.from(e.target.files ?? [])));
                  e.target.value = "";
                }}
              />
              <div
                className={`media-drop${dragging ? " over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => fileInput.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInput.current?.click(); }}
              >
                <strong>
                  {current
                    ? `Drop photos, video or whole folders into ${current.name}`
                    : "Drop photos, video or whole folders here"}
                </strong>
                <span className="muted">
                  or click to choose files. Drop a folder and its subfolders are recreated here
                  with the same names, so a client's shoot arrives filed rather than in a heap.
                  Files upload one after another, and you can leave this page once they are done.
                  Nothing restricted — see above.
                </span>
              </div>
            </>
          )}

          {uploads.length > 0 && (
            <div className="card upload-queue">
              <div className="storage-head">
                <h2>
                  {pending > 0
                    ? `Uploading — ${pending} left`
                    : uploads.some((u) => u.state === "error")
                      ? "Finished, with problems"
                      : "Uploaded"}
                </h2>
                <span className="spacer" />
                {!busy && (
                  <button className="sm" onClick={() => setUploads([])}>Clear</button>
                )}
              </div>
              <ul>
                {uploads.map((u) => (
                  <li key={u.id} className={u.state}>
                    <span className="up-name">
                      {u.name}
                      {u.folder && <span className="muted up-folder"> in {u.folder}</span>}
                    </span>
                    <span className="muted up-size">{formatBytes(u.size)}</span>
                    <span className={`chip ${u.state === "done" ? "ok" : u.state === "error" ? "err" : "busy"}`}>
                      {u.state === "queued" ? "Waiting"
                        : u.state === "filing" ? "Making folders"
                        : u.state === "reading" ? "Reading"
                        : u.state === "uploading" ? "Uploading"
                        : u.state === "done" ? "Stored"
                        : "Failed"}
                    </span>
                    {u.error && <span className="muted up-error">{u.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {loading ? (
            <p className="muted">Loading media…</p>
          ) : shown.length === 0 ? (
            <div className="card muted">
              {assets.length === 0
                // The brand comes first, because it is set from the title and
                // from the top bar — two places off this panel — and "nothing
                // stored yet" over a full library is the one message here that
                // would be a lie.
                ? activeBrandId
                  ? <>
                      No files are filed under{" "}
                      <strong>{brands.find((b) => b.id === activeBrandId)?.name ?? "this brand"}</strong>
                      {view.kind === "folder" ? " in this folder" : ""}.{" "}
                      <button className="link" onClick={() => selectBrand(null)}>
                        Show every brand
                      </button>
                    </>
                  : view.kind === "folder"
                  ? "This folder is empty. Upload into it, or select files elsewhere and move them here."
                  : view.kind === "unfiled"
                    ? "Everything is filed into a folder."
                    : mayUpload
                      ? "Nothing stored yet. Drop a few photos, videos or a whole folder above and they are yours to publish whenever you want."
                      : "Nothing stored yet."
                : "Nothing matches this filter."}
            </div>
          ) : (
            <div className="media-grid">
              {shown.map((a) => {
                // Undefined both while signing is in flight and when there is
                // no derivative to sign. `Thumb` tells those apart; nothing
                // here needs to.
                const key = thumbPath(a);
                const url = key === null ? undefined : urls.get(key);
                // Not the storage figure above — how many posts this file went out in.
                const usedIn = a.posts?.length ?? 0;
                const on = checked.has(a.id);
                return (
                  <button
                    key={a.id}
                    className={`media-tile${selected?.id === a.id ? " on" : ""}${on ? " checked" : ""}`}
                    aria-pressed={picking ? on : undefined}
                    onClick={() => {
                      if (!picking) { setSelected(a); return; }
                      setChecked((prev) => {
                        const next = new Set(prev);
                        if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                        return next;
                      });
                    }}
                  >
                    <span className="thumb">
                      <Thumb asset={a} url={url} alt={a.original_filename ?? ""} />
                      {isVideo(a) && <span className="badge">{duration(a.duration_seconds) ?? "video"}</span>}
                      {picking && <span className="tick" aria-hidden="true">{on ? "✓" : ""}</span>}
                    </span>
                    <span className="media-name">{a.original_filename ?? "Untitled"}</span>
                    <span className="muted media-meta">
                      {formatBytes(a.byte_size)}
                      {a.width ? ` · ${a.width}×${a.height}` : ""}
                    </span>
                    {/* Which networks would take it. Instagram and Facebook
                        disagree about the same file often enough that the
                        answer belongs on the tile, not two screens later. */}
                    <MediaFitRow results={fit.get(a.id)} kind={isVideo(a) ? "video" : "photo"} />
                    <span className={usedIn > 0 ? "chip ok" : "chip warn"}>
                      {usedIn > 0 ? `${usedIn} post${usedIn === 1 ? "" : "s"}` : "Never published"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {more && !loading && (
            <p className="row" style={{ justifyContent: "center", margin: "1rem 0 2rem" }}>
              <button onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Show more"}
              </button>
            </p>
          )}
        </div>
      </div>

      {importing && (
        <div className="modal-backdrop" onClick={() => setImporting(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="import-title"
               onClick={(e) => e.stopPropagation()}>
            <h2 id="import-title">Import from a cloud drive</h2>
            <p className="muted modal-lede">
              Paste a download link from OneDrive, Google Drive or Dropbox and we fetch the file
              straight into your library — it never passes through this device. The link has to be
              one the drive generated for the file itself; a "share this folder" page will not work.
              {current && <> It will be filed in <strong>{current.path}</strong>.</>}
            </p>
            <p className="notice" style={{ marginBottom: ".8rem" }}>
              An imported file lands in your library exactly as an uploaded one does, and the same
              restrictions apply to it — see <Link to="/terms#restricted">what may not be
              uploaded</Link>.
            </p>

            <label htmlFor="import-url">File link</label>
            <input id="import-url" value={importUrl} placeholder="https://…"
                   onChange={(e) => setImportUrl(e.target.value)} />

            {error && <div className="err modal-error">{error}</div>}

            <div className="row modal-actions">
              <button className="primary" disabled={importBusy || !importUrl}
                      onClick={async () => {
                        setImportBusy(true);
                        setError(null);
                        try {
                          await importMedia({
                            workspaceId: workspaceId!,
                            url: importUrl,
                            folderId: uploadFolderId,
                            brandId: activeBrandId,
                          });
                          setImportUrl("");
                          setImporting(false);
                          // Straight back to a fresh list, so the new file is
                          // visibly there rather than merely reported.
                          await refresh();
                        } catch (e: any) {
                          setError(e.message ?? String(e));
                          void logError("media_import", e, { step: "import", workspaceId });
                        } finally {
                          setImportBusy(false);
                        }
                      }}>
                {importBusy ? "Importing…" : "Import"}
              </button>
              <button onClick={() => setImporting(false)} disabled={importBusy}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {folderDialog && workspaceId && (
        <FolderDialog
          mode={folderDialog.mode}
          initial={folderDialog.folder?.name ?? ""}
          parentLabel={
            folderDialog.parent
              ? folders.find((f) => f.id === folderDialog.parent)?.path ?? "Top level"
              : "Top level"
          }
          busy={folderBusy}
          onClose={() => setFolderDialog(null)}
          onSave={async (name) => {
            await runFolderAction(async () => {
              if (folderDialog.mode === "create") {
                const created = await createMediaFolder(workspaceId, name, folderDialog.parent);
                // Straight into the new folder: making one is almost always the
                // first half of putting something in it.
                setView({ kind: "folder", id: created.id });
              } else if (folderDialog.folder) {
                await renameMediaFolder(folderDialog.folder.id, name);
              }
            });
            setFolderDialog(null);
          }}
        />
      )}

      {confirmFolder && (
        <div className="modal-backdrop" onClick={() => setConfirmFolder(null)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="del-folder-title"
               onClick={(e) => e.stopPropagation()}>
            <h2 id="del-folder-title">Delete "{confirmFolder.name}"?</h2>
            <p className="muted modal-lede">
              Deleting a folder never deletes a file. Anything inside it —{" "}
              {confirmFolder.subtree_file_count} file
              {confirmFolder.subtree_file_count === 1 ? "" : "s"} in this folder and below it —
              moves up to{" "}
              <strong>
                {confirmFolder.parent_id
                  ? folders.find((f) => f.id === confirmFolder.parent_id)?.name ?? "the level above"
                  : "Unfiled"}
              </strong>{" "}
              and stays in your library.
            </p>
            <div className="row modal-actions">
              <button className="danger" disabled={folderBusy}
                      onClick={async () => {
                        await runFolderAction(async () => {
                          await deleteMediaFolder(confirmFolder.id, true);
                          setView(confirmFolder.parent_id
                            ? { kind: "folder", id: confirmFolder.parent_id }
                            : { kind: "all" });
                        });
                        setConfirmFolder(null);
                      }}>
                {folderBusy ? "Deleting…" : "Delete the folder, keep the files"}
              </button>
              <button onClick={() => setConfirmFolder(null)} disabled={folderBusy}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <MediaDetail
          asset={selected}
          url={fullUrl}
          folders={folders}
          brands={brands}
          fit={fit.get(selected.id)}
          mayDelete={mayDelete}
          mayFile={mayFile}
          onClose={() => setSelected(null)}
          onMoved={async (folderId) => {
            await runFolderAction(() => moveMediaAssets([selected.id], folderId));
            setSelected(null);
          }}
          onFiled={async (brandId) => {
            await runFolderAction(() => setMediaBrand([selected.id], brandId));
            setSelected(null);
          }}
          onDeleted={async () => {
            setSelected(null);
            await refresh().catch(() => {});
          }}
        />
      )}
    </>
  );
}

/** Making and renaming are the same dialog with a different verb. */
function FolderDialog({
  mode, initial, parentLabel, busy, onSave, onClose,
}: {
  mode: "create" | "rename";
  initial: string;
  parentLabel: string;
  busy: boolean;
  onSave: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="folder-title"
           onClick={(e) => e.stopPropagation()}>
        <h2 id="folder-title">{mode === "create" ? "New folder" : "Rename folder"}</h2>
        {mode === "create" && (
          <p className="muted modal-lede">It goes inside <strong>{parentLabel}</strong>.</p>
        )}

        <label htmlFor="folder-name">Name</label>
        <input id="folder-name" value={name} autoFocus maxLength={80}
               placeholder="BluePeak Coffee"
               onChange={(e) => setName(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === "Enter" && name.trim()) void onSave(name.trim());
               }} />

        <div className="row modal-actions">
          <button className="primary" disabled={busy || !name.trim()}
                  onClick={() => void onSave(name.trim())}>
            {busy ? "Saving…" : mode === "create" ? "Create" : "Rename"}
          </button>
          <button onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function MediaDetail({
  asset, url, folders, brands, fit, mayDelete, mayFile, onClose, onMoved, onFiled, onDeleted,
}: {
  asset: MediaAsset;
  url: string | undefined;
  folders: MediaFolder[];
  brands: Brand[];
  /** Per-network verdicts; undefined while they are still being fetched. */
  fit: ValidationResult[] | undefined;
  mayDelete: boolean;
  mayFile: boolean;
  onClose: () => void;
  onMoved: (folderId: string | null) => Promise<void>;
  onFiled: (brandId: string | null) => Promise<void>;
  onDeleted: () => Promise<void>;
}) {
  const zone = useZone();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const posts = asset.posts ?? [];
  // A post that has already gone out does not need the bytes any more — it
  // keeps its record and loses the preview (migration 20260828000900). A post
  // that has not gone out still needs them to publish at all, and the database
  // refuses that; saying so up front beats a button that only ever fails.
  const pending = posts.filter(
    (p) => p.status === "draft" || p.status === "scheduled" || p.status === "publishing",
  );
  const locked = pending.length > 0;
  const sent = posts.length - pending.length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="media-title">{asset.original_filename ?? "Untitled"}</h2>

        {url && (isVideo(asset)
          ? <video className="preview" src={url} controls />
          : <img className="preview" src={url} alt={asset.original_filename ?? ""} />)}

        <dl className="kv">
          <dt>Type</dt><dd>{asset.mime_type}</dd>
          <dt>Size</dt><dd>{formatBytes(asset.byte_size)}</dd>
          {asset.width && <><dt>Dimensions</dt><dd>{asset.width}×{asset.height}</dd></>}
          {asset.duration_seconds && (
            <><dt>Length</dt><dd>{duration(asset.duration_seconds)}</dd></>
          )}
          <dt>Folder</dt>
          <dd>
            {mayFile ? (
              <>
                <label className="visually-hidden" htmlFor="asset-folder">Folder</label>
                <select id="asset-folder" value={asset.folder_id ?? ""}
                        onChange={(e) => void onMoved(e.target.value || null)}>
                  <option value="">Unfiled</option>
                  {folders.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
                </select>
              </>
            ) : (
              folders.find((f) => f.id === asset.folder_id)?.path ?? "Unfiled"
            )}
          </dd>
          {/* Beside the folder rather than anywhere else on the panel: they are
              the two answers to "where does this live", and reading one without
              the other is how a file ends up in the right shoot and the wrong
              brand. Absent entirely when the workspace has no brands. */}
          {brands.length > 0 && (
            <>
              <dt>Brand</dt>
              <dd>
                {mayFile ? (
                  <>
                    <label className="visually-hidden" htmlFor="asset-brand">Brand</label>
                    <select id="asset-brand" value={asset.brand_id ?? ""}
                            onChange={(e) => void onFiled(e.target.value || null)}>
                      <option value="">Unfiled</option>
                      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </>
                ) : (
                  brands.find((b) => b.id === asset.brand_id)?.name ?? "Unfiled"
                )}
              </dd>
            </>
          )}
          <dt>Uploaded</dt><dd>{new Date(asset.created_at).toLocaleString()}</dd>
        </dl>

        <h3>Where it fits</h3>
        <MediaFitDetail results={fit} kind={isVideo(asset) ? "video" : "photo"} />

        <h3>Used in</h3>
        {posts.length === 0 ? (
          <p className="muted">
            This file was uploaded but never published — use it in a post below and it goes out
            without being uploaded again.
          </p>
        ) : (
          <ul className="media-posts">
            {posts.map((p) => (
              <li key={p.id}>
                <Link to={`/posts/${p.id}`}>
                  {p.title || p.caption?.slice(0, 60) || "Untitled post"}
                </Link>
                <span className={`chip ${p.status === "published" ? "ok" : "warn"}`}>{p.status}</span>
                <span className="muted">
                  {p.scheduled_at
                    ? `scheduled ${formatInZoneWithName(p.scheduled_at, zone)}`
                    : formatInZone(p.created_at, zone)}
                </span>
                {/* Per-network outcome, because partial success is the normal
                    case (ADR-0007) and the asset page should not flatten it. */}
                <span className="media-deliveries">
                  {(p.deliveries ?? []).map((d) => (
                    <span key={d.id} className="muted" title={d.error_message ?? undefined}>
                      {PLATFORM_LABEL[d.platform as Platform] ?? d.platform}: {d.status}
                      {d.provider_url && <> · <a href={d.provider_url} target="_blank" rel="noreferrer">view</a></>}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}

        {deleteError && <div className="err modal-error">{deleteError}</div>}

        {confirming ? (
          <div className="row modal-actions">
            <span className="muted">
              Delete this file permanently and free {formatBytes(asset.byte_size)}?
              {sent > 0 && (
                <> The {sent === 1 ? "post" : `${sent} posts`} below stay{sent === 1 ? "s" : ""} exactly
                  as {sent === 1 ? "it is" : "they are"} — what went out is already on the network,
                  and only the preview here goes.</>
              )}
            </span>
            <button className="danger" disabled={deleting}
                    onClick={async () => {
                      setDeleting(true);
                      setDeleteError(null);
                      try {
                        await deleteMedia(asset);
                        await onDeleted();
                      } catch (e: any) {
                        setDeleteError(e.message ?? String(e));
                        void logError("app", e, { step: "delete_media" });
                        setConfirming(false);
                      } finally {
                        setDeleting(false);
                      }
                    }}>
              {deleting ? "Deleting…" : "Delete"}
            </button>
            <button onClick={() => setConfirming(false)} disabled={deleting}>Keep it</button>
          </div>
        ) : (
          <div className="row modal-actions">
            {/* The point of a library: publish what is already here, without a
                second upload. Compose picks the asset up from the query string. */}
            <button className="primary" onClick={() => navigate(`/compose?media=${asset.id}`)}>
              Use in a new post
            </button>
            {mayDelete && (
              <button
                className="danger"
                disabled={locked}
                title={!locked ? undefined
                  : pending.length === 1
                    ? "Still needed by a post that hasn't gone out — publish or delete it first"
                    : `Still needed by ${pending.length} posts that haven't gone out `
                      + "— publish or delete them first"}
                onClick={() => setConfirming(true)}
              >
                Delete
              </button>
            )}
            <button onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
